/* EqFoonote Class
 * Takes a module and adds references/footnote links and back-links as needed.
 * Back-links are the leading footnote marker (1, *), not a trailing "Return to" control.
 * ==== Usage Notes ====
 * Use the following attributes to markup your HTML to indicate the following
 * Add the "eq-footnote" class around the module or div surrounding a group of notes and references
 *
 * Attributes
 * data-footnote-title: Sets the title of the group of footnotes for accessibility
 * If this attribute isn't used, it will attempt to derive the title from the first h2 of the module
 *
 * data-footnote-ref: marker the reference should link to (number or *)
 *
 * data-footnote: marker associated with the footnote (number or *)
 * Optional, if footnotes are contained within an element all next to each other, add class 'eq-footnote-list' to the parent element
 *
 * Markers
 * Numbered markers (1, 2, 1., [1]) and asterisk markers (*, *., [*]) are both supported.
 * Visible <sup> text / a leading footnote marker is the identity of the note. A conflicting
 * data-footnote / data-footnote-ref value is ignored so a local "*" is not treated as "1".
 * A <sup> first looks for a matching footnote in the same module (typically the .footnote
 * block just below). If none is found, it links to the first matching footnote on the page.
 *
 * Example HTML
 * <div class="module eq-footnote" data-footnote-title="Module Title">
 *  <p class="example-footnote" data-footnote-ref="1">Example data text</p>
 *  <p class="example-source" data-footnote="1">Example source text</p>
 * </div>
 */
class EqFootnote {
    constructor(selector = '.eq-footnote') {
        this.modules = Array.from(document.querySelectorAll(selector));
        this.module_footnotes = new WeakMap();
        this.page_footnotes = new Map();

        this.modules.forEach((module, module_index) => {
            module._footnote_title = this.module_title(module, module_index);
            this.wrap_bare_asterisks(module);
        });

        this.modules.forEach((module) => {
            this.collect_footnotes(module);
        });

        this.modules.forEach((module) => {
            this.build_references(module);
        });

        this.add_backlinks();
    }

    module_title(module, module_index) {
        let title = '';
        if (module.dataset.footnoteTitle) {
            title = ' ' + module.dataset.footnoteTitle;
        }
        const row_title = module.querySelector('h2');
        if (!title && row_title) {
            title = ' ' + row_title.innerText;
        }
        if (!title) {
            title = ` Module ${module_index}`;
        }
        return title;
    }

    normalize_marker(raw) {
        if (raw == null) {
            return '';
        }
        let marker = String(raw).trim();
        marker = marker.replace(/^\[|\]$/g, '').replace(/^\(|\)$/g, '');
        marker = marker.replace(/[.\)]+$/g, '').trim();
        return marker;
    }

    safe_key(marker) {
        if (marker === '*') {
            return 'asterisk';
        }
        return String(marker).replace(/[^\w-]/g, '') || 'mark';
    }

    marker_from_sup(sup) {
        const visible = this.normalize_marker(sup.textContent);
        if (visible) {
            return visible;
        }
        return this.normalize_marker(sup.dataset.footnoteRef);
    }

    marker_from_footnote(footnote, index) {
        const leading_sup = footnote.querySelector('sup');
        if (leading_sup && this.is_leading_node(footnote, leading_sup)) {
            const from_sup = this.normalize_marker(leading_sup.textContent);
            if (from_sup) {
                return from_sup;
            }
        }
        const text = footnote.textContent.trim();
        const match = text.match(/^(\*|\d+)/);
        if (match) {
            return match[1];
        }
        if (footnote.dataset.footnote) {
            return this.normalize_marker(footnote.dataset.footnote);
        }
        return index != null ? String(index + 1) : '';
    }

    is_leading_node(container, node) {
        let current = node;
        while (current && current !== container) {
            let sibling = current.previousSibling;
            while (sibling) {
                if (sibling.nodeType === Node.ELEMENT_NODE && !sibling.classList.contains('anchor')) {
                    return false;
                }
                if (sibling.nodeType === Node.TEXT_NODE && sibling.nodeValue.trim() !== '') {
                    return false;
                }
                sibling = sibling.previousSibling;
            }
            current = current.parentNode;
        }
        return true;
    }

    is_inside_footnote(el) {
        return !!el.closest('[data-footnote], .eq-footnote-list > *, .footnote p, .footnote li, .footnote');
    }

    is_footnote_marker_sup(sup) {
        return this.is_inside_footnote(sup);
    }

    wrap_bare_asterisks(root) {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                const parent = node.parentElement;
                if (!parent) {
                    return NodeFilter.FILTER_REJECT;
                }
                if (parent.closest('sup, a, script, style, .footnote, .eq-footnote-list')) {
                    return NodeFilter.FILTER_REJECT;
                }
                if (!/\*\s*$/.test(node.nodeValue) || !/[^*\s]/.test(node.nodeValue)) {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        });
        const nodes = [];
        while (walker.nextNode()) {
            nodes.push(walker.currentNode);
        }
        nodes.forEach((node) => {
            const match = node.nodeValue.match(/^(.*?)(\*)(\s*)$/);
            if (!match) {
                return;
            }
            const parent = node.parentNode;
            if (match[1]) {
                parent.insertBefore(document.createTextNode(match[1]), node);
            }
            const sup = document.createElement('sup');
            sup.textContent = match[2];
            parent.insertBefore(sup, node);
            if (match[3]) {
                parent.insertBefore(document.createTextNode(match[3]), node);
            }
            parent.removeChild(node);
        });
    }

    footnote_elements(module) {
        const found = [];
        const seen = new Set();
        const add = (node_list) => {
            node_list.forEach((el) => {
                if (!seen.has(el)) {
                    seen.add(el);
                    found.push(el);
                }
            });
        };
        add(module.querySelectorAll('[data-footnote]'));
        add(module.querySelectorAll('.eq-footnote-list > *'));
        add(module.querySelectorAll('.footnote p, .footnote li'));
        return found;
    }

    collect_footnotes(module) {
        const title = module._footnote_title;
        const module_id = this.slugify(title);
        const local = new Map();
        const footnotes = this.footnote_elements(module);

        footnotes.forEach((footnote, index) => {
            const marker = this.marker_from_footnote(footnote, index);
            if (!marker) {
                return;
            }
            const safe = this.safe_key(marker);
            const existing = footnote.querySelector(':scope > .anchor');
            let footnote_id = existing && existing.id ? existing.id : `${module_id}-footnote-${safe}`;
            if (!existing) {
                let duplicate = 2;
                while (document.getElementById(footnote_id)) {
                    footnote_id = `${module_id}-footnote-${safe}-${duplicate++}`;
                }
                this.add_anchor(footnote, footnote_id);
            }
            const entry = { el: footnote, id: footnote_id, marker, title };
            // Visible marker wins. Do not also index a conflicting data-footnote
            // (e.g. <p data-footnote="1"><sup>*</sup>...) or a local "*" steals "1".
            if (!local.has(marker)) {
                local.set(marker, entry);
            }
            if (!this.page_footnotes.has(marker)) {
                this.page_footnotes.set(marker, entry);
            }
        });

        this.module_footnotes.set(module, local);
    }

    find_footnote(module, marker) {
        const local = this.module_footnotes.get(module);
        if (local && local.has(marker)) {
            const entry = local.get(marker);
            if (entry && entry.marker === marker) {
                return entry;
            }
        }
        const page_entry = this.page_footnotes.get(marker);
        if (page_entry && page_entry.marker === marker) {
            return page_entry;
        }
        return null;
    }

    reference_elements(module) {
        const found = [];
        const seen = new Set();
        const add = (el) => {
            if (!el || seen.has(el)) {
                return;
            }
            if (this.is_footnote_marker_sup(el)) {
                return;
            }
            if (el.closest('a[href^="#"]')) {
                return;
            }
            if (el.querySelector && el.querySelector('a[href^="#"]')) {
                return;
            }
            seen.add(el);
            found.push(el);
        };
        module.querySelectorAll('[data-footnote-ref]').forEach(add);
        module.querySelectorAll('sup').forEach(add);
        return found;
    }

    build_references(module) {
        const title = module._footnote_title;
        const module_id = this.slugify(title);
        const references = this.reference_elements(module);

        references.forEach((ref) => {
            const visible_marker = this.marker_from_sup(ref);
            const attr_marker = this.normalize_marker(ref.dataset.footnoteRef);
            // Prefer the visible marker (so <sup>1</sup> is never bound to a local "*").
            // Only use data-footnote-ref when it matches the visible text, or the
            // visible marker has no footnote anywhere on the page.
            let target = visible_marker ? this.find_footnote(module, visible_marker) : null;
            if (!target && attr_marker && attr_marker !== visible_marker) {
                target = this.find_footnote(module, attr_marker);
            }
            if (!target) {
                return;
            }
            const marker = visible_marker || attr_marker || target.marker;
            const label_marker = marker === '*' ? 'asterisk' : marker;
            const footnote_link = document.createElement('a');
            footnote_link.innerText = ref.innerText.trim() || marker;
            footnote_link.setAttribute('aria-label', `Reference to${title} footnote ${label_marker}`);
            footnote_link.setAttribute('href', `#${target.id}`);
            const safe = this.safe_key(marker);
            let ref_id = `${module_id}-footnote-${safe}-ref`;
            let ref_count = 2;
            while (document.getElementById(ref_id)) {
                ref_id = `${module_id}-footnote-${safe}-ref-${ref_count++}`;
            }
            ref.replaceChildren(footnote_link);
            this.add_anchor(ref, ref_id);
            if (!target.first_ref_id) {
                target.first_ref_id = ref_id;
            }
        });
    }

    wrap_marker_as_backlink(footnote, marker, link_back) {
        const leading_sup = footnote.querySelector('sup');
        if (leading_sup && this.is_leading_node(footnote, leading_sup)) {
            if (leading_sup.querySelector('a')) {
                return false;
            }
            link_back.textContent = leading_sup.textContent.trim() || marker;
            leading_sup.replaceChildren(link_back);
            return true;
        }

        let node = footnote.firstChild;
        while (node) {
            if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('anchor')) {
                node = node.nextSibling;
                continue;
            }
            if (node.nodeType === Node.TEXT_NODE) {
                const match = node.nodeValue.match(/^(\s*)(\*|\d+)([\s\S]*)$/);
                if (match) {
                    const parent = node.parentNode;
                    if (match[1]) {
                        parent.insertBefore(document.createTextNode(match[1]), node);
                    }
                    link_back.textContent = match[2];
                    parent.insertBefore(link_back, node);
                    if (match[3]) {
                        parent.insertBefore(document.createTextNode(match[3]), node);
                    }
                    parent.removeChild(node);
                    return true;
                }
            }
            break;
        }
        return false;
    }

    add_backlinks() {
        const seen = new Set();
        const add_link = (entry) => {
            if (!entry || seen.has(entry.el) || entry.el.querySelector('.link-back')) {
                return;
            }
            seen.add(entry.el);
            if (!entry.first_ref_id) {
                return;
            }
            const link_back = document.createElement('a');
            link_back.setAttribute('href', `#${entry.first_ref_id}`);
            link_back.classList.add('link-back');
            link_back.addEventListener('click', (event) => {
                const target = document.getElementById(entry.first_ref_id);
                if (!target) {
                    return;
                }
                event.preventDefault();
                target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                const ref_link = target.parentElement && target.parentElement.querySelector('a');
                if (ref_link && typeof ref_link.focus === 'function') {
                    ref_link.focus({ preventScroll: true });
                }
                if (history.replaceState) {
                    history.replaceState(null, '', `#${entry.first_ref_id}`);
                }
            });
            if (!this.wrap_marker_as_backlink(entry.el, entry.marker, link_back)) {
                return;
            }
        };

        this.modules.forEach((module) => {
            const local = this.module_footnotes.get(module);
            if (!local) {
                return;
            }
            local.forEach((entry) => add_link(entry));
        });
        this.page_footnotes.forEach((entry) => add_link(entry));
    }

    slugify(str) {
        return String(str)
            .trim()
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    // Adds a span with the class "anchor" as the first child of an element to aid in proper positioining when jumping to footnotes
    add_anchor(element, id) {
        if (element.querySelector(':scope > .anchor')) {
            return;
        }
        const anchor = document.createElement('span');
        anchor.classList.add('anchor');
        anchor.setAttribute('id', id);
        element.insertAdjacentElement('afterbegin', anchor);
    }
}
